import {
  mdiAccountLockOutline,
  mdiAccountLockOpenOutline,
  mdiCancel,
  mdiEmailArrowRightOutline,
  mdiEyeOutline,
} from "@mdi/js"
import type { RelationshipStatus } from "@kontextwerk/control-web-client"
import type { TableRowAction } from "@kontextwerk/web-sdk/core/layout/table"
import { USER_ACTION_TOOLTIP_CLASS, userActionTooltip } from "../_logic/actionTooltips"
import type { InvitationRow, MemberRow, UserRow } from "../_shared/_logic/table"

export const OPEN_USER_ACTION_ID = "open-record"
export const RESEND_INVITATION_ACTION_ID = "resend-invitation"

type Props = {
  row: UserRow
  /** core.memberships manage of the confirmed context. */
  canManage: boolean
  /** The actor holds the owner role without support delegation. */
  actorIsOwner: boolean
  /** Rows with a running command; their commands stay disabled until it settles. */
  pendingRowIds: ReadonlySet<string>
  onOpenUserPage: (row: MemberRow) => void
  onChangeAccess: (row: MemberRow, status: RelationshipStatus) => void
  onResendInvitation: (row: InvitationRow) => void
  onRevokeInvitation: (row: InvitationRow) => void
}

/**
 * Owners are only changed by owners, nobody changes their own membership here, and a status
 * change needs the membership facts of the row. The server stays the deciding owner.
 */
const canChangeAccess = (row: MemberRow, canManage: boolean, actorIsOwner: boolean) =>
  canManage &&
  !row.isSelf &&
  row.statusKey !== null &&
  row.roleId !== null &&
  row.membershipRevision !== null &&
  (row.roleKind !== "owner" || actorIsOwner)

export default function buildRowActions({
  row,
  canManage,
  actorIsOwner,
  pendingRowIds,
  onOpenUserPage,
  onChangeAccess,
  onResendInvitation,
  onRevokeInvitation,
}: Props): TableRowAction[] {
  const pending = pendingRowIds.has(row.id)

  if (row.kind === "invitation") {
    if (!canManage) return []

    return [
      {
        id: RESEND_INVITATION_ACTION_ID,
        label: "Einladung erneut senden",
        icon: mdiEmailArrowRightOutline,
        tooltip: userActionTooltip("resend-invitation"),
        tooltipClassName: USER_ACTION_TOOLTIP_CLASS,
        disabled: pending,
        pending,
        onClick: () => onResendInvitation(row),
      },
      {
        id: "revoke-invitation",
        label: "Einladung zurückziehen",
        icon: mdiCancel,
        tooltip: userActionTooltip("revoke-invitation"),
        tooltipClassName: USER_ACTION_TOOLTIP_CLASS,
        tone: "danger",
        disabled: pending,
        onClick: () => onRevokeInvitation(row),
      },
    ]
  }

  const actions: TableRowAction[] = [
    {
      id: OPEN_USER_ACTION_ID,
      label: "Benutzer öffnen",
      icon: mdiEyeOutline,
      tooltip: userActionTooltip("open"),
      tooltipClassName: USER_ACTION_TOOLTIP_CLASS,
      onClick: () => onOpenUserPage(row),
    },
  ]

  if (canChangeAccess(row, canManage, actorIsOwner)) {
    const suspended = row.statusKey === "suspended"
    actions.push({
      id: suspended ? "unsuspend" : "suspend",
      label: suspended ? "Entsperren" : "Sperren",
      icon: suspended ? mdiAccountLockOpenOutline : mdiAccountLockOutline,
      tooltip: userActionTooltip(suspended ? "unsuspend" : "suspend"),
      tooltipClassName: USER_ACTION_TOOLTIP_CLASS,
      tone: suspended ? "default" : "danger",
      disabled: pending,
      pending,
      onClick: () => onChangeAccess(row, suspended ? "active" : "suspended"),
    })
  }

  return actions
}
